import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import TabWrapper from './TabWrapper';
import { getListCodebaseByDao } from 'redux/actions/daoUI/codebase.action';
import CodebaseDetailModal from './CodebaseDetailModal';

const SolutionsManagementTab = ({ daoOrganizerAddress, chainId, daoId, daoInfo }) => {
  const [codebases, setCodebases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedCodebase, setSelectedCodebase] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);

  useEffect(() => {
    const fetchCodebases = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          daoId,
          chainId,
          page,
          limit
        };
        const data = await getListCodebaseByDao(params)();
        setCodebases(data?.data || []);
        setTotal(data?.pagination?.total || 0);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    if (daoOrganizerAddress && chainId) fetchCodebases();
  }, [daoOrganizerAddress, chainId, page, limit, daoId]);

  const handleCloseModal = () => {
    setOpenDetail(false);
    setIsCreateMode(false);
    setSelectedCodebase(null);
  };

  const reloadCodebases = () => {
    setPage(1);
    const params = {
      daoId,
      chainId,
      page: 1,
      limit
    };
    getListCodebaseByDao(params)()
      .then(data => {
        setCodebases(data?.data || []);
        setTotal(data?.pagination?.total || 0);
      })
      .catch(err => {
        setError(err);
      });
  };

  return (
    <TabWrapper daoOrganizerAddress={daoOrganizerAddress} chainId={chainId}>
      <Box sx={{ p: 4 }}>
        <Typography
          variant="h6"
          sx={{ mb: 3, pb: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          Solutions - Management
        </Typography>

        {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <button
            onClick={() => { setIsCreateMode(true); setOpenDetail(true); }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#00ffe0',
              color: '#181c22',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Create New Codebase
          </button>
        </Box> */}

        {loading && (
          <Typography sx={{ color: '#e7e3e3' }}>Loading...</Typography>
        )}
        
        {error && (
          <Typography sx={{ color: 'red' }}>{error.message}</Typography>
        )}

        {!loading && !error && (
          <>
            <Box sx={{ 
              overflowX: 'auto',
              '& table': {
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                mt: 2,
                bgcolor: '#23272f',
                color: '#00ffe0',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 2px 8px #0006'
              }
            }}>
              <table>
                <thead>
                  <tr style={{ backgroundColor: '#181c22' }}>
                    <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Name</th>
                    <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Created</th>
                    <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {codebases.map((codebase, idx) => (
                    <tr key={codebase._id}
                      style={{ 
                        borderBottom: '1px solid #00ffe0',
                        background: idx % 2 === 0 ? '#23272f' : '#181c22',
                        transition: 'background 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#26313a'}
                      onMouseOut={e => e.currentTarget.style.background = idx % 2 === 0 ? '#23272f' : '#181c22'}
                      onClick={() => { setSelectedCodebase(codebase); setOpenDetail(true); }}
                    >
                      <td style={{ padding: '12px', color: '#fff' }}>{codebase.name}</td>
                      <td style={{ padding: '12px', color: '#fff' }}>
                        {codebase.createdAt ? new Date(codebase.createdAt).toLocaleDateString() : 'Not specified'}
                      </td>
                      <td style={{ padding: '12px', color: '#fff' }}>
                        {codebase.updatedAt ? new Date(codebase.updatedAt).toLocaleDateString() : 'Not specified'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            {codebases.length === 0 && (
              <Typography sx={{ 
                textAlign: 'center',
                py: 5,
                color: '#6c757d'
              }}>
                No codebases found. Click "Create New Codebase" to get started.
              </Typography>
            )}

            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#00ffe0',
                  color: '#181c22',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  opacity: page === 1 ? 0.5 : 1
                }}
              >
                Previous Page
              </button>
              <Typography sx={{ color: '#fff' }}>Page {page}</Typography>
              <button 
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / limit)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#00ffe0',
                  color: '#181c22',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  opacity: page >= Math.ceil(total / limit) ? 0.5 : 1
                }}
              >
                Next Page
              </button>
            </Box>
          </>
        )}

        <CodebaseDetailModal 
          open={openDetail} 
          onClose={handleCloseModal} 
          codebase={selectedCodebase}
          isCreateMode={isCreateMode}
          daoId={daoId}
          chainId={chainId}
          daoInfo={daoInfo}
          onSuccess={reloadCodebases}
        />
      </Box>
    </TabWrapper>
  );
};

export default SolutionsManagementTab;
